import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTripsListStore } from "../store/tripsListStore";
import { useLibraryStore, isOwnPoi, isOwnTag } from "../store/libraryStore";
import { useCollectionsStore } from "../store/collectionsStore";
import { Button } from "../components/Button";
import { TripCard } from "../components/TripCard";
import { CreateTripModal } from "../components/CreateTripModal";
import { ShareTripModal } from "../components/ShareTripModal";

interface FeatureCardProps {
  icon: string;
  iconBgClass: string;
  iconColorClass: string;
  title: string;
  count: number;
  countLabel: string;
  description: string;
  onClick: () => void;
}

function FeatureCard({
  icon,
  iconBgClass,
  iconColorClass,
  title,
  count,
  countLabel,
  description,
  onClick,
}: FeatureCardProps) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-start p-5 rounded-2xl bg-white border border-border cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300 text-left"
    >
      <div
        className={`flex items-center justify-center size-14 rounded-xl bg-gradient-to-br ${iconBgClass} mb-4`}
      >
        <span
          className={`material-symbols-outlined text-2xl ${iconColorClass}`}
        >
          {icon}
        </span>
      </div>
      <h3 className="text-base font-semibold text-primary mb-1">{title}</h3>
      <p className="text-2xl font-bold text-primary mb-1">
        {count}{" "}
        <span className="text-sm font-normal text-secondary">{countLabel}</span>
      </p>
      <p className="text-sm text-secondary mb-3">{description}</p>
      <span className="text-sm font-medium text-primary group-hover:text-blue-600 transition-colors">
        Open {title} <span className="ml-1">→</span>
      </span>
    </button>
  );
}

export function TripsPage() {
  const navigate = useNavigate();
  const { trips, createTrip, deleteTrip } = useTripsListStore();
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const collections = useCollectionsStore((s) => s.collections);
  // Home stat cards are the caller's own library only — POIs/tags made
  // visible via a shared trip belong in the trip sidebar, not here.
  const ownPois = useMemo(() => pois.filter(isOwnPoi), [pois]);
  const ownTags = useMemo(() => tags.filter(isOwnTag), [tags]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [shareTripId, setShareTripId] = useState<string | null>(null);

  const handleCreate = async (
    name: string,
    startDate: string,
    dayCount: number,
  ) => {
    const tripId = await createTrip({ name, startDate, dayCount });
    navigate(`/trips/${tripId}`);
  };

  const handleDelete = async (id: string) => {
    await deleteTrip(id);
  };

  const handleShare = (tripId: string) => {
    setShareTripId(tripId);
  };

  const shareTrip = trips.find((t) => t.id === shareTripId);

  return (
    <div className="min-h-screen bg-page" data-testid="trips-page">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <svg
                width="28"
                height="28"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-lg font-bold text-primary tracking-tight">
                PlotPlanGo
              </span>
            </div>
          </div>

          <Button
            onClick={() => navigate("/settings")}
            variant="icon"
            icon="settings"
            title="Settings"
            aria-label="Settings"
          />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Manage Your Library section */}
        <div className="mb-10">
          <h2 className="text-lg font-bold text-primary mb-1">
            Manage Your Library
          </h2>
          <p className="text-sm text-secondary mb-5">
            Organize your places, tags, and collections
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon="location_on"
              iconBgClass="from-blue-100 to-blue-200"
              iconColorClass="text-blue-600"
              title="Library"
              count={ownPois.length}
              countLabel={ownPois.length === 1 ? "POI" : "POIs"}
              description="Your saved places and accommodations"
              onClick={() => navigate("/library")}
            />
            <FeatureCard
              icon="label"
              iconBgClass="from-purple-100 to-purple-200"
              iconColorClass="text-purple-600"
              title="Tags"
              count={ownTags.length}
              countLabel={ownTags.length === 1 ? "tag" : "tags"}
              description="Categorize and filter your places"
              onClick={() => navigate("/tags")}
            />
            <FeatureCard
              icon="folder_special"
              iconBgClass="from-amber-100 to-amber-200"
              iconColorClass="text-amber-600"
              title="Collections"
              count={collections.length}
              countLabel={
                collections.length === 1 ? "collection" : "collections"
              }
              description="Group places for quick access"
              onClick={() => navigate("/collections")}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border mb-8" />

        {/* Section header */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-primary mb-1">My Trips</h2>
          <p className="text-sm text-secondary">
            {trips.length === 0
              ? "Create your first trip to get started"
              : `${trips.length} ${trips.length === 1 ? "trip" : "trips"} planned`}
          </p>
        </div>

        {/* Trips grid or empty state */}
        {trips.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex items-center justify-center size-20 rounded-2xl bg-slate-100 mb-6">
              <span className="material-symbols-outlined text-4xl text-slate-400">
                luggage
              </span>
            </div>
            <h2 className="text-lg font-bold text-primary mb-2">
              No trips yet
            </h2>
            <p className="text-sm text-secondary mb-6 text-center max-w-sm">
              Start planning your next adventure by creating a new trip. Add
              destinations, accommodations, and build your perfect itinerary.
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-first-trip-button"
              variant="primary"
              size="lg"
              icon="add"
            >
              Create Your First Trip
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onDelete={handleDelete}
                onShare={handleShare}
              />
            ))}

            {/* Add trip placeholder card */}
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="add-new-trip-button"
              className="group flex flex-col items-center justify-center min-h-[180px] rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 cursor-pointer transition-all hover:border-slate-400 hover:bg-slate-100/50"
            >
              <div className="flex items-center justify-center size-12 rounded-xl bg-white border border-slate-200 mb-3 transition-all group-hover:shadow-md group-hover:scale-105">
                <span className="material-symbols-outlined text-2xl text-slate-400 group-hover:text-slate-600">
                  add
                </span>
              </div>
              <span className="text-sm font-medium text-slate-500 group-hover:text-slate-700">
                Add New Trip
              </span>
            </button>
          </div>
        )}
      </main>

      <CreateTripModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />

      {shareTrip && (
        <ShareTripModal
          isOpen={!!shareTripId}
          onClose={() => setShareTripId(null)}
          tripId={shareTrip.id}
          tripName={shareTrip.name}
          isOwner={shareTrip.isOwner}
        />
      )}
    </div>
  );
}
